// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function TraderLabTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="TraderLab"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of
        “TraderLab” (the “App”). By downloading, installing, accessing, or using
        the App, you agree to be bound by these Terms. If you do not agree, you
        must uninstall the App and discontinue all use. Where required by
        platform rules or local law, additional terms may apply; in case of
        conflict with App Store or Google Play terms, the platform terms may
        prevail to the extent required.
      </p>

      <div className={s.title}>2) Educational Purpose Only / No Financial Advice</div>
      <p>
        TraderLab is an educational mobile application for learning trading
        concepts through courses, video lessons, materials, and other content
        with elements of personal branding from featured traders. The App does
        not provide financial, investment, tax, or legal advice, does not
        provide trading signals, and does not promise profits, performance, or
        specific outcomes. Any charts, examples, scenarios, strategies, or trade
        ideas shown in the App are for illustration and general education only.
        Markets involve risk, including the risk of loss of capital. You are
        solely responsible for your financial decisions and should consult
        qualified professionals before making investment or trading choices.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. The App is
        intended for adults interested in general trading education and is not
        directed to children where parental consent is required by law. By using
        the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Accounts and Access</div>
      <p>
        The App may allow you to access content with or without a user account.
        If account-based access is provided (for example, via Firebase
        Authentication or similar), you are responsible for maintaining the
        confidentiality of your credentials and for all activities that occur
        under your account. You must promptly notify us of any suspected
        unauthorized access or security incident. We may suspend, restrict, or
        disable access where we reasonably believe there has been a breach of
        security or a violation of these Terms.
      </p>
      <p>
        Certain features—such as saving progress across devices, tracking
        completed lessons, or restoring purchases—may require an account or
        reliance on platform identity mechanisms (for example, Apple ID or
        Google Play).
      </p>

      <div className={s.title}>5) Subscriptions, Trials, and Billing</div>
      <p>
        The App may offer auto-renewable subscriptions or other in-app products
        (“Subscriptions”) that unlock premium content or features, such as full
        course libraries, additional materials, or advanced tools. Purchases are
        processed by Apple or Google via your store account; we do not receive
        your payment card numbers. Pricing, taxes, billing periods, and available
        offers are determined by the platform and may vary by region.
      </p>
      <p>
        If a free trial is offered, it will convert into a paid Subscription
        unless cancelled in your account settings at least 24 hours before the
        end of the trial. You can manage or cancel Subscriptions directly in
        your device subscription settings. Deleting the App does not
        automatically cancel a Subscription. Refunds, grace periods, and other
        billing matters are handled in accordance with App Store or Google Play
        policies.
      </p>

      <div className={s.title}>6) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We receive limited entitlement
        data—such as product identifiers, trial eligibility, renewal success,
        expiration dates, or cancellations—to determine whether you have access
        to certain content or features. An internet connection may be required
        to validate or refresh entitlements after long offline periods. If a
        transaction is reversed, refunded, or flagged as fraudulent, related
        entitlements may be revoked or adjusted.
      </p>

      <div className={s.title}>7) Courses, Lessons, and Educational Content</div>
      <p>
        All courses, modules, videos, texts, quizzes, downloadable materials,
        and other content in the App are provided for general educational
        purposes. We may update, add, reorganize, or remove content from time to
        time. Completion of a course or lesson in the App does not grant any
        formal certification, licensing, regulatory approval, or professional
        qualification. You remain responsible for complying with any regulatory
        requirements in your jurisdiction if you engage in trading or investing.
      </p>
      <p>
        Markets and financial information change over time. Content may refer to
        past conditions, examples, or instruments that no longer reflect current
        market reality. You must not rely solely on App content when making
        trading or investment decisions.
      </p>

      <div className={s.title}>8) Downloads and Offline Use</div>
      <p>
        Where available, the App may allow you to download lessons or materials
        for offline use. Downloaded content remains subject to these Terms and
        may become inaccessible if a Subscription expires, your entitlement is
        revoked, or you sign out. Offline files are generally stored within the
        App’s storage area on your device and may be removed by the system, by
        you, or during updates. We do not guarantee indefinite offline access to
        any specific item.
      </p>

      <div className={s.title}>9) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, personal, and non-commercial
        educational purposes and not to:
      </p>
      <ul>
        <li>
          treat App content as personalized investment advice, trading signals,
          or recommendations to buy, sell, or hold any financial instrument;
        </li>
        <li>
          copy, record, redistribute, or publicly display course content outside
          the App without explicit permission;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract source code except
          where permitted by law;
        </li>
        <li>
          circumvent technical protections, access controls, or fair-use limits
          on streaming or downloads;
        </li>
        <li>
          use the App to transmit spam, abusive, or illegal content, or to
          harass or harm others;
        </li>
        <li>
          use automated tools to scrape, copy, or systematically download
          content beyond normal usage patterns;
        </li>
        <li>
          use the App in violation of applicable laws, regulations, or
          third-party rights.
        </li>
      </ul>

      <div className={s.title}>10) Content and Intellectual Property</div>
      <p>
        All courses, videos, texts, graphics, logos, and other materials in the
        App are owned by the publisher or its licensors and are protected by
        intellectual property laws. Subject to these Terms and any Subscription
        limitations, you are granted a limited, non-exclusive, non-transferable,
        revocable license to access and use the App and its content solely for
        personal educational purposes. You may not copy, redistribute, resell,
        or publicly perform content outside the App without explicit written
        permission.
      </p>
      <p>
        Any trademarks, service marks, or brand elements used in the App remain
        the property of their respective owners and may not be used to imply
        endorsement or affiliation without permission.
      </p>

      <div className={s.title}>11) Third-Party Services</div>
      <p>
        The App may integrate third-party services such as Firebase (for
        authentication, storage, remote configuration, analytics, messaging, and
        crash reporting), RevenueCat (for subscription management), content
        delivery networks, and video streaming or hosting providers. Use of the
        App is subject to the terms and policies of these providers and your
        platform account (Apple, Google). We are not responsible for outages,
        data loss, or service limitations caused solely by third-party services
        outside our reasonable control.
      </p>

      <div className={s.title}>12) No Guarantees of Availability or Performance</div>
      <p>
        We aim to keep TraderLab available and up to date but do not guarantee
        uninterrupted or error-free operation. The App and its content may
        change, be updated, or be removed from time to time. Certain features
        may depend on network connectivity, device capabilities, OS version, and
        third-party services, which can affect performance and availability.
        Temporary unavailability or reduced quality of streaming, downloads, or
        analytics does not give rise to any guarantee, refund obligation, or
        compensation beyond what platform policies provide.
      </p>

      <div className={s.title}>13) Market Risk and Disclaimers</div>
      <p>
        Trading and investing in financial markets involve substantial risk,
        including the risk of losing money. Past performance is not indicative
        of future results. Any examples, backtests, or hypothetical scenarios
        presented in the App are illustrative only and may not reflect real
        trading conditions. You should never trade with money you cannot afford
        to lose and should always perform your own due diligence and seek
        professional advice.
      </p>
      <p>
        The App does not monitor your personal financial situation, portfolio,
        or risk tolerance and therefore cannot tailor content as individual
        advice. You remain fully responsible for any trades or investments you
        choose to make.
      </p>

      <div className={s.title}>14) Limitation of Liability</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        To the extent allowed by law, we are not liable for indirect,
        incidental, consequential, or punitive damages, loss of profits, trading
        losses, data loss, or business interruption arising from or related to
        your use of or inability to use the App, or from your reliance on any
        educational content.
      </p>
      <p>
        Where liability cannot be excluded, our total aggregate liability to you
        for direct damages arising out of or relating to the App will be limited
        to the amount you paid for Subscriptions during the twelve (12) months
        prior to the claim, or the minimum amount permitted by applicable law if
        greater. Some jurisdictions do not allow certain limitations, so parts
        of this section may not apply to you.
      </p>

      <div className={s.title}>15) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of
        your misuse of the App, your trading or investment decisions, your
        breach of these Terms, or your violation of any rights of a third party.
      </p>

      <div className={s.title}>16) Termination</div>
      <p>
        We may suspend or terminate your access to the App, or to specific
        features, at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law or platform rules. You
        may stop using the App at any time by uninstalling it and, where
        applicable, cancelling your Subscription via store settings. Sections
        that by their nature should survive termination (for example,
        intellectual property, limitation of liability, indemnification, and
        disclaimers) will continue to apply.
      </p>

      <div className={s.title}>17) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features, adjust free and premium
        offerings, or modify these Terms from time to time. The “Last updated”
        date at the top of this document indicates when these Terms were last
        revised. Material changes may be highlighted in the App or communicated
        by other reasonable means. By continuing to use the App after changes
        take effect, you accept the updated Terms. If you do not agree, you must
        stop using the App and uninstall it.
      </p>

      <div className={s.title}>18) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        TraderLab, which explains how information is collected, used, stored,
        and shared, including analytics and crash diagnostics. In case of
        conflict between these Terms and the Privacy Policy regarding data
        processing, the Privacy Policy will govern how we handle personal data,
        while these Terms govern your use of the App and its content. You should
        review the Privacy Policy regularly to understand current practices.
      </p>

      <div className={s.title}>19) Governing Law and Disputes</div>
      <p>
        Applicable law and dispute resolution mechanisms may depend on your
        country of residence and mandatory consumer protection rules. Where
        permitted, you agree to first contact us to attempt to resolve any
        dispute informally. Platform-specific dispute or refund procedures
        (Apple, Google) may also apply to billing matters. Nothing in these
        Terms limits any non-waivable rights you may have under local law.
      </p>

      <div className={s.title}>20) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account- or
        purchase-specific matters.
      </p>
    </TermOfUseLayout>
  );
}
